import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Check, PackageSearch } from 'lucide-react';

/**
 * BrandSearchDropdown — Premium searchable dropdown untuk memilih brand klien.
 * Menggantikan native <datalist> dengan UI yang modern, animasi halus,
 * highlight pencarian, dan keyboard navigation.
 *
 * Props:
 *   brands: string[]                — daftar nama brand
 *   value: string                   — nilai pencarian saat ini
 *   onChange: (value: string) => void
 *   onSelect: (brand: string) => void  — dipanggil saat user memilih brand
 *   placeholder?: string
 */
export default function BrandSearchDropdown({
  brands = [],
  value = '',
  onChange,
  onSelect,
  placeholder = 'Ketik nama brand...',
}) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter brands by search value
  const filtered = brands.filter((b) =>
    b.toLowerCase().includes((value || '').toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset focused index when filtered list changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [value]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-brand-item]');
      if (items[focusedIndex]) {
        items[focusedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    if (!open) setOpen(true);
  };

  const handleSelect = useCallback(
    (brand) => {
      onChange(brand);
      onSelect?.(brand);
      setOpen(false);
      setFocusedIndex(-1);
    },
    [onChange, onSelect]
  );

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filtered.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filtered[focusedIndex]) {
          handleSelect(filtered[focusedIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Highlight matched text in brand name
  const highlightMatch = (text) => {
    if (!value) return text;
    const idx = text.toLowerCase().indexOf(value.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-[#D4AF37]/25 text-[#6e4c06] font-bold rounded-sm px-0.5">
          {text.slice(idx, idx + value.length)}
        </span>
        {text.slice(idx + value.length)}
      </>
    );
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input Field */}
      <div
        className={`
          relative flex items-center gap-2 
          rounded-2xl border-2 bg-white
          transition-all duration-300 ease-out
          ${
            open
              ? 'border-[#D4AF37] shadow-[0_0_0_4px_rgba(212,175,55,0.12)] ring-0'
              : 'border-[#E6D5B8] hover:border-[#D4AF37]/50 shadow-sm'
          }
        `}
      >
        <Search
          className={`absolute left-4 w-5 h-5 transition-colors duration-200 ${
            open ? 'text-[#8C6208]' : 'text-[#A39171]'
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="
            w-full pl-12 pr-12 py-4 
            bg-transparent text-[#302621] 
            font-semibold text-[15px] 
            placeholder:text-[#B8A898] placeholder:font-normal
            outline-none rounded-2xl
          "
        />

        {/* Clear / Chevron */}
        <div className="absolute right-3 flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
                setOpen(true);
              }}
              className="
                w-7 h-7 rounded-full flex items-center justify-center
                text-[#A39171] hover:text-[#302621] hover:bg-[#F9F5EC]
                transition-all duration-200
              "
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen((v) => !v);
              if (!open) inputRef.current?.focus();
            }}
            className="
              w-7 h-7 rounded-full flex items-center justify-center
              text-[#A39171] hover:text-[#8C6208] hover:bg-[#F9F5EC]
              transition-all duration-200
            "
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-300 ${
                open ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Dropdown Panel */}
      <div
        className={`
          absolute z-50 mt-2 w-full
          bg-white rounded-2xl border border-[#E6D5B8]
          shadow-[0_12px_40px_rgba(48,38,33,0.12)]
          overflow-hidden
          transition-all duration-250 ease-out origin-top
          ${
            open
              ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 scale-y-95 -translate-y-1 pointer-events-none'
          }
        `}
      >
        {/* Header hint */}
        <div className="px-4 pt-3 pb-2 border-b border-[#F0E6D4]">
          <p className="text-[11px] font-semibold text-[#A39171] uppercase tracking-wider">
            {filtered.length > 0
              ? `${filtered.length} brand ditemukan`
              : 'Tidak ada hasil'}
          </p>
        </div>

        {/* Options List */}
        <div
          ref={listRef}
          className="max-h-[260px] overflow-y-auto overscroll-contain"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#E6D5B8 transparent',
          }}
        >
          {filtered.length === 0 ? (
            <div className="py-10 text-center">
              <PackageSearch className="w-10 h-10 text-[#E6D5B8] mx-auto mb-3" />
              <p className="text-sm font-semibold text-[#A39171]">
                Brand tidak ditemukan
              </p>
              <p className="text-xs text-[#C4B29E] mt-1">
                Coba ketik nama brand lain
              </p>
            </div>
          ) : (
            filtered.map((brand, i) => {
              const isSelected = brand.toLowerCase() === (value || '').toLowerCase();
              const isFocused = i === focusedIndex;

              return (
                <button
                  key={brand}
                  type="button"
                  data-brand-item
                  onClick={() => handleSelect(brand)}
                  onMouseEnter={() => setFocusedIndex(i)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-left
                    transition-all duration-150 ease-out
                    border-b border-[#F9F5EC] last:border-b-0
                    group cursor-pointer
                    ${
                      isFocused
                        ? 'bg-gradient-to-r from-[#F9F5EC] to-[#FFF9ED]'
                        : 'hover:bg-[#FDFAF4]'
                    }
                    ${isSelected ? 'bg-[#F9F5EC]' : ''}
                  `}
                >
                  {/* Brand initial avatar */}
                  <div
                    className={`
                      w-9 h-9 rounded-xl flex items-center justify-center
                      text-xs font-black uppercase shrink-0
                      transition-all duration-200
                      ${
                        isSelected || isFocused
                          ? 'bg-gradient-to-br from-[#D4AF37] to-[#8C6208] text-white shadow-md shadow-[#D4AF37]/20'
                          : 'bg-[#F4EDE0] text-[#8C6208] group-hover:bg-[#EDE3D0]'
                      }
                    `}
                  >
                    {brand.charAt(0)}
                  </div>

                  {/* Brand name */}
                  <span
                    className={`
                      flex-1 text-sm truncate
                      transition-colors duration-150
                      ${
                        isSelected
                          ? 'font-bold text-[#302621]'
                          : isFocused
                          ? 'font-semibold text-[#302621]'
                          : 'font-medium text-[#5C4D3E] group-hover:text-[#302621]'
                      }
                    `}
                  >
                    {highlightMatch(brand)}
                  </span>

                  {/* Selected check */}
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                  )}

                  {/* Hover arrow indicator */}
                  {!isSelected && isFocused && (
                    <div className="w-6 h-6 rounded-full bg-[#F4EDE0] flex items-center justify-center shrink-0 animate-in fade-in duration-200">
                      <ChevronDown className="w-3.5 h-3.5 text-[#8C6208] -rotate-90" />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Footer hint for keyboard navigation */}
        {filtered.length > 0 && (
          <div className="px-4 py-2.5 border-t border-[#F0E6D4] bg-[#FDFAF4]">
            <div className="flex items-center gap-3 text-[10px] text-[#B8A898]">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-[#E6D5B8] rounded text-[#8C6208] font-bold shadow-sm">↑↓</kbd>
                navigasi
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-[#E6D5B8] rounded text-[#8C6208] font-bold shadow-sm">Enter</kbd>
                pilih
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-[#E6D5B8] rounded text-[#8C6208] font-bold shadow-sm">Esc</kbd>
                tutup
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
